import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Building, Calendar, Sparkles, Plus, X, Search, ChevronLeft, ChevronRight, SlidersHorizontal, Lock, ExternalLink, FileText, Briefcase } from "lucide-react";
import { StateLocalDirectory } from "@/components/StateLocalDirectory";
import UsageMeter from "@/components/UsageMeter";

const fmt = (n) => "$" + Math.round(Number(n || 0)).toLocaleString();
const relTime = (ts) => {
  if (!ts) return null;
  const s = Math.max(0, Math.floor(Date.now() / 1000 - ts));
  if (s < 90) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};
const CERT_TO_SETASIDE = { "8(a)": "8A", "SDVOSB": "SDVOSBC", "WOSB": "WOSB", "WBE": "WOSB", "HUBZone": "HZC" };
const recColor = (r) => r === "Bid" ? "text-emerald-600 bg-emerald-50 border-emerald-200" : r === "Avoid" ? "text-red-600 bg-red-50 border-red-200" : "text-amber-600 bg-amber-50 border-amber-200";

export default function Opportunities() {
  const navigate = useNavigate();
  const [resp, setResp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feed, setFeed] = useState("open");
  const [sources, setSources] = useState([]);
  const [q, setQ] = useState("");
  const [setAside, setSetAside] = useState("");
  const [myCert, setMyCert] = useState("");
  const [saTouched, setSaTouched] = useState(false);
  const [agency, setAgency] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [sort, setSort] = useState("amount_desc");
  const [page, setPage] = useState(1);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState(null);
  const [grantDetail, setGrantDetail] = useState(null);
  const [grantLoading, setGrantLoading] = useState(false);
  const [usageRefresh, setUsageRefresh] = useState(0);

  const fetchData = useCallback(async (p = page, f = feed) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ feed: f, q, agency, sort, page: p, page_size: 8 });
      if (minAmount) params.set("min_amount", minAmount);
      if (maxAmount) params.set("max_amount", maxAmount);
      if (setAside && f === "open") params.set("set_aside", setAside);
      const r = await api.get(`/opportunities?${params.toString()}`);
      setResp(r.data);
      if (r.data.sources) setSources(r.data.sources);
    } catch (e) { toast.error("Could not load opportunities"); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, agency, minAmount, maxAmount, sort, feed, setAside]);

  useEffect(() => {
    fetchData(1);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agency, sort, feed, setAside]);

  useEffect(() => {
    if (!details) { setGrantDetail(null); setGrantLoading(false); return; }
    const t = details.opp_type;
    if (t === "grant" || t === "solicitation") {
      setGrantDetail(null); setGrantLoading(true);
      const url = t === "grant" ? `/opportunities/grant/${details.source_id}` : `/opportunities/sam/${details.source_id}`;
      api.get(url).then((r) => setGrantDetail(r.data)).catch(() => {}).finally(() => setGrantLoading(false));
    } else { setGrantDetail(null); setGrantLoading(false); }
  }, [details]);

  const applyFilters = () => { setPage(1); fetchData(1); };
  const goPage = (p) => { setPage(p); fetchData(p); };
  const selectFeed = (id) => { setFeed(id); setQ(""); setAgency(""); setMinAmount(""); setMaxAmount(""); setSetAside(""); setSaTouched(false); setPage(1); };

  // Pre-select the set-aside filter from the contractor's own certifications.
  useEffect(() => {
    api.get("/profile").then((r) => {
      const certs = r.data?.profile?.certifications || [];
      const code = certs.map((c) => CERT_TO_SETASIDE[c]).find(Boolean) || "";
      setMyCert(code);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (feed === "open" && myCert && !saTouched) setSetAside(myCert);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed, myCert]);

  const analyze = async (opp) => {
    setDetails(null); setSelected(opp); setAnalysis(null); setAnalyzing(true);
    try { const r = await api.post("/opportunities/analyze", { opportunity: opp }); setAnalysis(r.data); }
    catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Analysis failed"));
      setSelected(null);
    }
    finally { setAnalyzing(false); setUsageRefresh((n) => n + 1); }
  };
  const addToCrm = async (opp, a) => {
    try {
      await api.post("/bids", { title: opp.title, agency: opp.agency, amount: opp.amount || 0, deadline: opp.deadline, qualification_score: a?.qualification_score ?? null, win_probability: a?.win_probability ?? null, source_id: opp.source_id, notes: opp.description || "" });
      toast.success("Added to Bid CRM");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Bid CRM is a Pro feature — upgrade to track this." : "Could not add");
    }
  };

  return (
    <div className="space-y-6" data-testid="opportunities-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Opportunities</h1>
        <p className="text-slate-500 mt-1">Live contract activity matched to your trade · source: <span className="font-data text-slate-700">{resp?.source === "usaspending" ? "USAspending.gov" : resp?.source === "grants.gov" ? "Grants.gov" : resp?.source === "sample" ? "Sample data" : "—"}</span></p>
      </div>

      {resp?.can_analyze && <UsageMeter refresh={usageRefresh} className="max-w-sm" />}

      {/* Source tabs */}
      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-1" data-testid="source-tabs">
        {(sources.length ? sources : [{ id: "federal", label: "Federal", connected: true }]).map((s) => (
          <button key={s.id} onClick={() => selectFeed(s.id)} data-testid={`source-tab-${s.id}`}
            className={`relative px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${feed === s.id ? "text-slate-900" : "text-slate-400 hover:text-slate-600"}`}>
            <span className="inline-flex items-center gap-1.5">
              {s.label}
              {!s.connected && <span className="text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-400 rounded px-1.5 py-0.5">soon</span>}
            </span>
            {feed === s.id && <span className="absolute left-0 right-0 -bottom-[5px] h-0.5 bg-emerald-900 rounded-full" />}
          </button>
        ))}
      </div>

      <StateLocalDirectory />

      {feed === "open" && resp && !loading && (
        <div className="flex items-center gap-2 text-sm" data-testid="open-feed-freshness">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1 font-medium">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            {resp.total} live open bids
          </span>
          {resp.last_refreshed && <span className="text-slate-400">Feed updated {relTime(resp.last_refreshed)}</span>}
        </div>
      )}

      {resp?.plan_limited && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3" data-testid="free-limit-banner">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Lock className="h-5 w-5 text-amber-600 shrink-0" />
              <p className="text-sm text-amber-800">Free plan includes {resp.free_limit} opportunity matches/mo and locks AI analysis. Upgrade to Growth for unlimited matches + AI scoring.</p>
            </div>
            <button onClick={() => navigate("/dashboard/billing?plan=growth")} data-testid="free-banner-upgrade" className="shrink-0 bg-emerald-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-emerald-800 transition-colors">Upgrade</button>
          </div>
          <div data-testid="opp-usage-meter">
            <div className="flex justify-between text-xs text-amber-700 mb-1">
              <span>Opportunities used this month</span>
              <span className="font-data font-semibold">{resp.opp_used ?? 0} / {resp.free_limit}</span>
            </div>
            <div className="h-2 rounded-full bg-amber-100 overflow-hidden">
              <div className="h-full rounded-full bg-amber-500 transition-all" style={{ width: `${Math.min(100, ((resp.opp_used ?? 0) / resp.free_limit) * 100)}%` }} />
            </div>
          </div>
        </div>
      )}

      {resp && resp.feed_connected === false ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center" data-testid="feed-not-connected">
          <Building className="h-10 w-10 mx-auto text-slate-300" />
          <h3 className="font-heading text-xl font-semibold mt-4">This source isn't connected yet</h3>
          <p className="text-slate-500 text-sm mt-2 max-w-md mx-auto">{resp.feed_message}</p>
        </div>
      ) : (
      <>
      {/* Filter bar */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3" data-testid="opportunity-filters">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applyFilters()} placeholder="Search title, issuing organization, description…" className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40" />
          </div>
          <select data-testid="agency-filter" value={agency} onChange={(e) => setAgency(e.target.value)} className="rounded-md border border-slate-200 px-3 py-2 text-sm max-w-[220px]">
            <option value="">All issuing organizations</option>
            {(resp?.agencies || []).map((a) => <option key={a} value={a}>{a.length > 30 ? a.slice(0, 30) + "…" : a}</option>)}
          </select>
          <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-md border border-slate-200 px-3 py-2 text-sm">
            <option value="amount_desc">Amount: high → low</option>
            <option value="amount_asc">Amount: low → high</option>
            <option value="deadline">Deadline: soonest</option>
          </select>
          {feed === "open" && (
            <select data-testid="setaside-filter" value={setAside} onChange={(e) => { setSaTouched(true); setSetAside(e.target.value); }} className="rounded-md border border-slate-200 px-3 py-2 text-sm max-w-[220px]">
              <option value="">All set-asides</option>
              <option value="SBA">Total Small Business</option>
              <option value="8A">8(a)</option>
              <option value="SDVOSBC">SDVOSB (Veteran-owned)</option>
              <option value="WOSB">Women-Owned (WOSB)</option>
              <option value="EDWOSB">Economically Disadv. WOSB</option>
              <option value="HZC">HUBZone</option>
            </select>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs text-slate-400 inline-flex items-center gap-1"><SlidersHorizontal className="h-3.5 w-3.5" /> Amount</span>
          <input data-testid="min-amount" type="number" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} placeholder="Min $" className="w-28 rounded-md border border-slate-200 px-3 py-1.5 text-sm" />
          <input data-testid="max-amount" type="number" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} placeholder="Max $" className="w-28 rounded-md border border-slate-200 px-3 py-1.5 text-sm" />
          <button onClick={applyFilters} data-testid="apply-filters-button" className="bg-emerald-900 text-white rounded-md px-4 py-1.5 text-sm font-medium hover:bg-emerald-800 transition-colors">Apply</button>
          {resp && <span className="text-xs text-slate-400 ml-auto">{resp.total} results</span>}
        </div>
      </div>

      {loading ? (
        <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 border-[3px] border-slate-200 border-t-slate-900 rounded-full animate-spin" /></div>
      ) : (resp?.opportunities?.length || 0) === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-12 text-center text-slate-400" data-testid="opportunities-empty">
          {feed === "open"
            ? "Live open-bid feed is updating — check back shortly, or try broadening your set-aside / search filters."
            : "No opportunities match your filters."}
        </div>
      ) : (
        <div className="grid gap-4">
          {resp.opportunities.map((opp, i) => (
            <div key={opp.source_id + i} onClick={() => setDetails(opp)} role="button" tabIndex={0} className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 hover:shadow-md hover:border-emerald-300 transition-all cursor-pointer" data-testid={`opportunity-card-${i}`}>
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="font-heading text-lg font-semibold text-slate-900 truncate group-hover:text-emerald-700">{opp.title || "Federal Contract"}</h3>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-500">
                    <span className="inline-flex items-center gap-1.5"><Building className="h-4 w-4" />{opp.agency}</span>
                    {opp.location && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" />{opp.location}</span>}
                    {opp.posted_date && <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" />Posted {opp.posted_date}</span>}
                    {opp.deadline && <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" />{opp.opp_type === "solicitation" ? "Due " : ""}{opp.deadline}</span>}
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium mt-2.5">View details <ChevronRight className="h-3.5 w-3.5" /></span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {opp.opp_type === "grant" ? (
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Grant</span>
                  ) : (
                    <span className="font-data text-xl font-medium text-slate-900">{fmt(opp.amount)}</span>
                  )}
                  {resp?.can_analyze === false ? (
                    <button onClick={(e) => { e.stopPropagation(); navigate("/dashboard/billing?plan=growth"); }} data-testid={`analyze-locked-${i}`} className="bg-slate-100 text-slate-500 rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-200 transition-colors inline-flex items-center gap-2">
                      <Lock className="h-4 w-4" /> Upgrade to Analyze
                    </button>
                  ) : (
                    <button onClick={(e) => { e.stopPropagation(); analyze(opp); }} data-testid={`analyze-button-${i}`} className="bg-emerald-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-emerald-800 transition-colors inline-flex items-center gap-2">
                      <Sparkles className="h-4 w-4" /> AI Analyze
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {resp && resp.pages > 1 && (
        <div className="flex items-center justify-center gap-2" data-testid="pagination">
          <button disabled={page <= 1} onClick={() => goPage(page - 1)} data-testid="prev-page" className="p-2 rounded-md border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
          <span className="text-sm text-slate-600 font-data px-2">Page {resp.page} of {resp.pages}</span>
          <button disabled={page >= resp.pages} onClick={() => goPage(page + 1)} data-testid="next-page" className="p-2 rounded-md border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
        </div>
      )}
      </>
      )}

      {/* Details modal */}
      {details && (
        <div className="fixed inset-0 z-50 bg-emerald-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDetails(null)} data-testid="details-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-7">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  {details.opp_type === "grant"
                    ? <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Grant opportunity</span>
                    : details.opp_type === "solicitation"
                    ? <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-lime-50 text-lime-700 border border-lime-200">Open solicitation · accepting bids</span>
                    : <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">Contract award</span>}
                  <h3 className="font-heading text-xl font-semibold mt-3" data-testid="details-title">{details.title || "Federal Contract"}</h3>
                </div>
                <button onClick={() => setDetails(null)} data-testid="details-close" className="text-slate-400 hover:text-slate-900 shrink-0"><X className="h-5 w-5" /></button>
              </div>

              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4 mt-6">
                <DetailRow icon={Building} label="Issuing organization" value={grantDetail?.agency || details.agency} />
                {details.sub_agency && <DetailRow icon={Building} label="Sub-organization" value={details.sub_agency} />}
                {details.opp_type === "grant" ? (
                  <>
                    {grantDetail?.award_floor && <DetailRow icon={SlidersHorizontal} label="Award floor" value={grantDetail.award_floor} />}
                    {grantDetail?.award_ceiling && <DetailRow icon={SlidersHorizontal} label="Award ceiling" value={grantDetail.award_ceiling} />}
                    {grantDetail?.estimated_funding && <DetailRow icon={SlidersHorizontal} label="Estimated program funding" value={grantDetail.estimated_funding} />}
                    {grantDetail?.posting_date && <DetailRow icon={Calendar} label="Posted" value={grantDetail.posting_date} />}
                  </>
                ) : details.opp_type === "solicitation" ? (
                  <>
                    {details.set_aside && <DetailRow icon={Briefcase} label="Set-aside" value={details.set_aside} />}
                    {details.notice_type && <DetailRow icon={FileText} label="Notice type" value={details.notice_type} />}
                    {details.naics && <DetailRow icon={SlidersHorizontal} label="NAICS" value={details.naics} />}
                    {details.posted_date && <DetailRow icon={Calendar} label="Posted" value={details.posted_date} />}
                  </>
                ) : (
                  <DetailRow icon={SlidersHorizontal} label="Award amount" value={fmt(details.amount)} />
                )}
                {details.location && details.opp_type !== "grant" && <DetailRow icon={MapPin} label="Location / place of performance" value={details.location} />}
                {(grantDetail?.close_date || details.deadline) && <DetailRow icon={Calendar} label={details.opp_type === "grant" ? "Close date" : details.opp_type === "solicitation" ? "Response deadline" : "Period of performance end"} value={grantDetail?.close_date || details.deadline} />}
                {details.start_date && <DetailRow icon={Calendar} label="Start date" value={details.start_date} />}
                {details.incumbent && <DetailRow icon={Briefcase} label="Awardee / incumbent" value={details.incumbent} />}
                {(grantDetail?.number || details.number) && <DetailRow icon={FileText} label={details.opp_type === "grant" ? "Opportunity number" : details.opp_type === "solicitation" ? "Solicitation number" : "Award ID"} value={grantDetail?.number || details.number} />}
                {(grantDetail?.cfda || details.cfda) && <DetailRow icon={FileText} label="CFDA" value={grantDetail?.cfda || details.cfda} />}
                {(grantDetail?.contact_email || details.contact_email) && <DetailRow icon={Building} label="Agency contact" value={grantDetail?.contact_email || details.contact_email} />}
                {details.opp_type === "solicitation" && details.contact_name && <DetailRow icon={Briefcase} label="Contact name" value={details.contact_name} />}
                {details.opp_type === "solicitation" && details.contact_phone && <DetailRow icon={Building} label="Contact phone" value={details.contact_phone} />}
              </div>

              {grantLoading && (
                <div className="mt-6 flex items-center gap-2 text-sm text-slate-500" data-testid="grant-detail-loading">
                  <div className="h-4 w-4 border-2 border-slate-200 border-t-slate-900 rounded-full animate-spin" /> Loading full details…
                </div>
              )}

              {(grantDetail?.eligibility) && (
                <div className="mt-6">
                  <p className="text-sm font-semibold text-slate-900 mb-1.5 inline-flex items-center gap-2"><Briefcase className="h-4 w-4 text-slate-400" /> Who can apply</p>
                  <p className="text-sm text-slate-600 leading-relaxed" data-testid="grant-eligibility">{grantDetail.eligibility}</p>
                </div>
              )}

              {(grantDetail?.description || details.description) && (
                <div className="mt-6">
                  <p className="text-sm font-semibold text-slate-900 mb-1.5 inline-flex items-center gap-2"><FileText className="h-4 w-4 text-slate-400" /> Description</p>
                  <p className="text-sm text-slate-600 leading-relaxed" data-testid="details-description">{grantDetail?.description || details.description}</p>
                </div>
              )}

              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                {(grantDetail?.source_url || details.source_url) && (
                  <a href={grantDetail?.source_url || details.source_url} target="_blank" rel="noopener noreferrer" data-testid="details-source-link" className="flex-1 bg-white border border-emerald-900 text-emerald-900 rounded-md py-3 text-sm font-medium hover:bg-emerald-50 transition-colors inline-flex items-center justify-center gap-2">
                    View original posting <ExternalLink className="h-4 w-4" />
                  </a>
                )}
                {resp?.can_analyze === false ? (
                  <button onClick={() => navigate("/dashboard/billing?plan=growth")} data-testid="details-analyze-locked" className="flex-1 bg-slate-100 text-slate-500 rounded-md py-3 text-sm font-medium hover:bg-slate-200 transition-colors inline-flex items-center justify-center gap-2">
                    <Lock className="h-4 w-4" /> Upgrade to Analyze
                  </button>
                ) : (
                  <button onClick={() => analyze(details)} data-testid="details-analyze" className="flex-1 bg-emerald-900 text-white rounded-md py-3 text-sm font-medium hover:bg-emerald-800 transition-colors inline-flex items-center justify-center gap-2">
                    <Sparkles className="h-4 w-4" /> AI Analyze fit
                  </button>
                )}
              </div>
              <button onClick={() => { addToCrm(grantDetail ? { ...details, ...grantDetail } : details, null); }} data-testid="details-add-crm" className="mt-3 w-full bg-white border border-slate-200 text-slate-700 rounded-md py-2.5 text-sm font-medium hover:bg-slate-50 transition-colors inline-flex items-center justify-center gap-2">
                <Plus className="h-4 w-4" /> Add to Bid CRM
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Analysis modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-emerald-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="analysis-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-7" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <h3 className="font-heading text-xl font-semibold pr-4">{selected.title}</h3>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-900"><X className="h-5 w-5" /></button>
            </div>
            {analyzing ? (
              <div className="py-16 flex flex-col items-center gap-3">
                <div className="h-8 w-8 border-[3px] border-slate-200 border-t-slate-900 rounded-full animate-spin" />
                <p className="text-sm text-slate-500 font-data">Claude is analyzing your fit…</p>
              </div>
            ) : analysis && (
              <div className="mt-5 space-y-5" data-testid="analysis-result">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                    <p className="text-xs text-slate-500">Qualification</p>
                    <p className="font-data text-3xl font-medium text-emerald-600">{analysis.qualification_score}%</p>
                  </div>
                  <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                    <p className="text-xs text-slate-500 flex items-center justify-between">
                      <span>Win Probability</span>
                      {typeof analysis.confidence_score === "number" && (
                        <span className="text-[10px] font-semibold text-slate-400" data-testid="analysis-confidence">
                          {analysis.confidence_score}% conf.
                        </span>
                      )}
                    </p>
                    <p className="font-data text-3xl font-medium text-accent">{analysis.win_probability}%</p>
                  </div>
                </div>
                <div className={`rounded-xl border p-4 ${recColor(analysis.recommendation)}`}>
                  <p className="text-xs font-bold uppercase tracking-wider">Recommendation</p>
                  <p className="font-heading text-xl font-bold mt-1">{analysis.recommendation}</p>
                  <p className="text-sm mt-2 text-slate-600">{analysis.reasoning}</p>
                </div>
                {analysis.strengths?.length > 0 && (
                  <div data-testid="analysis-strengths"><p className="text-sm font-semibold text-slate-900 mb-2">Top Strengths</p>
                    <ul className="space-y-1">{analysis.strengths.map((s, i) => <li key={`str-${i}`} className="text-sm text-slate-700">✓ {s}</li>)}</ul></div>
                )}
                {(analysis.weaknesses?.length > 0 || analysis.gaps?.length > 0) && (
                  <div data-testid="analysis-weaknesses"><p className="text-sm font-semibold text-slate-900 mb-2">Top Weaknesses</p>
                    <ul className="space-y-1">{(analysis.weaknesses || analysis.gaps).map((s, i) => <li key={`wk-${i}`} className="text-sm text-slate-700">• {s}</li>)}</ul></div>
                )}
                {analysis.next_actions?.length > 0 && (
                  <div data-testid="analysis-next-actions" className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-4">
                    <p className="text-sm font-semibold text-emerald-900 mb-2">Recommended Next Actions</p>
                    <ol className="space-y-1 list-decimal list-inside">
                      {analysis.next_actions.map((s, i) => <li key={`na-${i}`} className="text-sm text-emerald-900">{s}</li>)}
                    </ol>
                  </div>
                )}
                <button onClick={() => { addToCrm(selected, analysis); setSelected(null); }} data-testid="add-to-crm-button" className="w-full bg-emerald-900 text-white rounded-md py-3 text-sm font-medium hover:bg-emerald-800 transition-colors inline-flex items-center justify-center gap-2">
                  <Plus className="h-4 w-4" /> Add to Bid CRM
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const DetailRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2.5">
    <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
    <div className="min-w-0">
      <p className="text-xs text-slate-400">{label}</p>
      <p className="text-sm font-medium text-slate-800 break-words">{value}</p>
    </div>
  </div>
);
